<template>
  <div>
    <CompanyFormDialog
      v-model="companyFormDialog"
      :edited-item="editedItem"
      :companies="companies"
      :edited-index="editedIndex"
      :edited-parent-index="editedParentIndex"
      @close="close"
      @company-updated="handleCompanyUpdated"
      @company-created="handleCompanyCreated"
      @everyday-offer-change="changeEveryDayOffer"
    >
      <v-col cols="12">
        <v-btn text @click="settingsDialog = true">
          <circle-button icon="mdi-cog-outline" class="mr-2"></circle-button>
          <span style="text-transform: initial">
            {{ $t("other_company_settings") }}
          </span>
        </v-btn>
        <br />

        <v-btn v-if="editedItem?.contract === 4" text @click="openPartner(editedItem)">
          <circle-button icon="mdi-handshake" class="mr-2"></circle-button>
          <span style="text-transform: initial">
            {{ $t("partner_restaurants_settings") }}
          </span>
        </v-btn>
        <br />

        <v-btn v-if="editedItem?.contract === 4" text @click="limitsDialog = true">
          <circle-button icon="mdi-speedometer" class="mr-2"></circle-button>
          <span style="text-transform: initial">
            {{ $t("limits_settings") }}
          </span>
        </v-btn>
      </v-col>
    </CompanyFormDialog>
    <global-confirm-box ref="confirmBox" />
    <page-header
      :title="$t('companies')"
      :subtitle="$t('manage_companies_branches')"
      icon="mdi-office-building"
      icon-color="primary"
    >
      <template #actions>
        <primary-button @click="companyFormDialog = true">
          <v-icon class="mr-2">mdi-plus-circle</v-icon>
          {{ $t("add_company") }}
        </primary-button>
      </template>
    </page-header>

    <page-toolbar :searchable="false">
      <template #filters>
        <v-col cols="auto">
          <autocomplete
            multiple
            item-text="name"
            item-value="id"
            :items="[
              { id: 1, name: $t('company_with_contract') },
              { id: 0, name: $t('company_without_contract') },
              { id: 2, name: $t('dining_hall') },
              { id: 3, name: $t('b2b') },
              { id: 4, name: $t('worker_more_restaurants') },
            ]"
            :label="$t('firm') + ' ' + $t('type').toLowerCase()"
            v-model="company_type"
            hide-details
            color="white"
            style="width: 400px"
          />
        </v-col>
        <v-col cols="auto">
          <text-field
            v-model="search"
            :label="$t('search')"
            kind="search"
            hide-details
            color="white"
          />
        </v-col>
        <v-col>
          <v-row no-gutters justify="end">
            <div>
              <v-row no-gutters class="toolbar-actions">
                <print-button @click="print()" />
                <circle-button
                  icon="mdi-store-search-outline"
                  @click="searchUserDialog = true"
                  color="primary"
                  :small="false"
                />
              </v-row>
            </div>
          </v-row>
        </v-col>
      </template>
    </page-toolbar>
    <div class="px-6 mt-2">
      <v-data-table
        ref="companyTable"
        :headers="headers"
        :items="filteredCompanies"
        :loading="isLoading"
        :loading-text="$t('loading_data')"
        class="ordera-box-shadow data-table-card"
        id="print"
        show-expand
        :single-expand="false"
        :expanded.sync="expandedRows"
        item-key="id"
        mobile-breakpoint="0"
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center">
            <v-avatar :color="getCompanyAvatarColor(item)" size="40" class="mr-3">
              <v-icon dark>mdi-office-building</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ toLatin(item.name) }}</div>
              <div class="text-caption text--secondary" v-if="item.city">
                <v-icon small>mdi-map-marker</v-icon>
                {{ item.city }}
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.shifts="{ item }">
          <div class="d-flex flex-wrap" style="gap: 4px">
            <v-chip
              v-for="n in 4"
              :key="n"
              v-if="getShiftEnabled(item, n)"
              color="orange"
              class="white--text"
            >
              {{ n }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.type="{ item }">
          <v-chip
            small
            :color="getCompanyTypeColor(item)"
            dark
            class="font-weight-medium"
          >
            {{ getCompanyTypeLabel(item) }}
          </v-chip>
        </template>

        <template v-slot:item.is_credit="{ item }">
          <div
            v-if="user.type == 'restaurant' || user.type == 'admin'"
            class="d-flex align-center"
            style="gap: 4px"
          >
            <v-checkbox
              v-model="item.is_credit"
              @change="setCredit(item)"
              hide-details
              dense
              class="ma-0 pa-0"
            ></v-checkbox>
            <circle-button
              icon="mdi-currency-eur"
              @click="openCredit(item)"
              color="success"
              :tooltip="$t('credit')"
              small
            ></circle-button>
          </div>
        </template>

        <template v-slot:item.users_count="{ item }">
          <v-chip
            small
            outlined
            color="primary"
            @click="goToUsers(item)"
            style="cursor: pointer"
          >
            <v-icon small class="mr-1">mdi-account-group</v-icon>
            {{ getTotalUsersCount(item) }}
          </v-chip>
        </template>

        <template v-slot:item.branches="{ item }">
          <v-btn
            v-if="item.children && item.children.length"
            text
            @click="toggleExpand(item)"
            class="expand-button black--text"
          >
            <span class="mr-1 text-subtitle-1"
              >{{ item.children.length }} {{ $t("branches").toLowerCase() }}</span
            >
            <v-icon class="black--text">
              {{
                isItemExpanded(item) ? "mdi-chevron-up-circle" : "mdi-chevron-down-circle"
              }}
            </v-icon>
          </v-btn>
        </template>

        <template v-slot:item.data-table-expand>
          <!-- Sakrivamo default expand ikonicu -->
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end" style="gap: 4px">
            <circle-button
              icon="mdi-pencil"
              @click="editItem(item)"
              :tooltip="$t('edit')"
              small
            />
            <circle-button
              icon="mdi-cog-outline"
              @click="openSettings(item)"
              :tooltip="$t('settings')"
              small
            />
            <circle-button
              v-if="item.contract === 4"
              icon="mdi-handshake"
              @click="openPartner(item)"
              :tooltip="$t('partners')"
              small
            />
            <circle-button
              v-if="item.contract === 4"
              icon="mdi-shield-account"
              :to="{
                name: 'company-id-limits',
                params: { id: item.id },
              }"
              :tooltip="$t('limits_settings')"
              small
            />
            <circle-button
              v-if="item.contract !== 4"
              icon="mdi-chart-bar"
              color="primary"
              @click="openMonthlyOrdersReport(item)"
              :tooltip="$t('monthly_report')"
              small
            />
            <circle-button
              icon="mdi-delete"
              @click="deleteItem(item)"
              color="error"
              :tooltip="$t('delete')"
              small
            />
          </div>
        </template>

        <template v-slot:expanded-item="{ item }">
          <template v-if="item.children">
            <td :colspan="headers.length" class="pa-0">
              <v-data-table
                :headers="headers"
                :search="search"
                class="child-table"
                :items="item.children || []"
                :items-per-page="item.children.length"
                hide-default-footer
                hide-default-header
                mobile-breakpoint="0"
              >
                <template v-slot:item.name="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar :color="getCompanyAvatarColor(item)" size="32" class="mr-3">
                      <v-icon dark>mdi-office-building</v-icon>
                    </v-avatar>
                    <div>
                      <div class="font-weight-medium text-body-2">
                        {{ toLatin(item.name) }}
                      </div>
                      <div class="text-caption text--secondary" v-if="item.city">
                        <v-icon class="mr-1">mdi-map-marker</v-icon>
                        {{ item.city }}
                      </div>
                    </div>
                  </div>
                </template>

                <template v-slot:item.shifts="{ item }">
                  <div class="d-flex flex-wrap" style="gap: 4px">
                    <v-chip v-if="item.shift_one" color="orange" class="white--text">
                      1
                    </v-chip>
                    <v-chip v-if="item.shift_two" color="orange" class="white--text">
                      2
                    </v-chip>
                    <v-chip v-if="item.shift_three" color="orange" class="white--text">
                      3
                    </v-chip>
                    <v-chip v-if="item.shift_four" color="orange" class="white--text">
                      4
                    </v-chip>
                  </div>
                </template>

                <template v-slot:item.type="{ item }">
                  <v-chip
                    dark
                    :color="getCompanyTypeColor(item)"
                    class="font-weight-medium"
                  >
                    {{ getCompanyTypeLabel(item) }}
                  </v-chip>
                </template>

                <template v-slot:item.is_credit="{ item }">
                  <div
                    v-if="user.type == 'restaurant' || user.type == 'admin'"
                    class="d-flex align-center"
                    style="gap: 4px"
                  >
                    <v-checkbox
                      v-model="item.is_credit"
                      @change="setCredit(item)"
                      hide-details
                      dense
                      class="ma-0 pa-0"
                    ></v-checkbox>
                    <circle-button
                      icon="mdi-currency-eur"
                      @click="openCredit(item)"
                      color="success"
                      :tooltip="$t('credit')"
                      small
                    ></circle-button>
                  </div>
                </template>

                <template v-slot:item.users_count="{ item }">
                  <v-chip
                    outlined
                    color="primary"
                    @click="goToUsers(item)"
                    style="cursor: pointer"
                  >
                    <v-icon class="mr-1">mdi-account-group</v-icon>
                    {{ item.users_count || 0 }}
                  </v-chip>
                </template>

                <template v-slot:item.branches="{ item }"> </template>

                <template v-slot:item.actions="{ item }">
                  <div class="d-flex justify-end" style="gap: 4px">
                    <circle-button
                      icon="mdi-pencil"
                      @click="editItem(item)"
                      :tooltip="$t('edit')"
                      small
                    ></circle-button>
                    <circle-button
                      icon="mdi-cog-outline"
                      @click="openSettings(item)"
                      :tooltip="$t('settings')"
                      small
                    ></circle-button>
                    <circle-button
                      v-if="item.contract === 4"
                      icon="mdi-handshake"
                      @click="openPartner(item)"
                      :tooltip="$t('partners')"
                      small
                    ></circle-button>
                    <circle-button
                      v-if="item.contract === 4"
                      icon="mdi-shield-account"
                      :to="{
                        name: 'company-id-limits',
                        params: { id: item.id },
                      }"
                      :tooltip="$t('limits_settings')"
                      small
                    ></circle-button>
                    <circle-button
                      v-if="item.contract !== 4"
                      icon="mdi-chart-bar"
                      color="primary"
                      @click="openMonthlyOrdersReport(item)"
                      :tooltip="$t('monthly_report')"
                      small
                    ></circle-button>
                    <circle-button
                      icon="mdi-delete"
                      @click="deleteItem(item)"
                      color="error"
                      :tooltip="$t('delete')"
                      small
                    ></circle-button>
                  </div>
                </template>
              </v-data-table>
            </td>
          </template>
        </template>
      </v-data-table>
    </div>

    <!-- Company Credit Dialog -->
    <company-credit-dialog
      v-model="creditDialog"
      :company="selectedCompany"
      @saved="initialize()"
    />

    <!-- Company Settings Dialog -->
    <company-settings-dialog
      v-model="settingsDialog"
      :company="selectedCompany"
      @saved="initialize()"
    />
    <search-user-dialog v-model="searchUserDialog" />

    <!-- Partner Restaurants Dialog -->
    <company-partner-dialog
      ref="partnerDialog"
      v-model="partnerDialog"
      :company="selectedCompany"
      @open-settings="openRestaurantSettings"
      @saved="initialize()"
    />
    <!-- Delivery Settings Dialog -->
    <company-delivery-settings-dialog
      v-model="deliverySettingsDialog"
      :selected-restaurant="selectedRestaurant"
      :selected-company="selectedCompany"
      @saved="reloadRestaurantSettings"
      @close="
        deliverySettingsDialog = false;
        user.type === 'restaurant' ? (partnerDialog = false) : '';
      "
    >
      <div class="mt-4">
        <v-btn text @click="settingsDialog = true">
          <circle-button icon="mdi-cog-outline" class="mr-2"></circle-button>
          <span style="text-transform: initial">
            {{ $t("other_company_settings") }}
          </span>
        </v-btn>
        <br />

        <v-btn
          v-if="selectedCompany?.contract === 4"
          text
          @click="openPartner(selectedCompany)"
        >
          <circle-button icon="mdi-handshake" class="mr-2"></circle-button>
          <span style="text-transform: initial">
            {{ $t("partner_restaurants_settings") }}
          </span>
        </v-btn>
        <br />

        <v-btn
          v-if="selectedCompany?.contract === 4"
          text
          :to="{ name: 'company-id-limits', params: { id: selectedCompany?.id } }"
        >
          <circle-button icon="mdi-speedometer" class="mr-2"></circle-button>
          <span style="text-transform: initial">
            {{ $t("limits_settings") }}
          </span>
        </v-btn>
      </div>
    </company-delivery-settings-dialog>

    <!-- Company Meals Monthly Orders Dialog -->
    <report-company-meals-taken-monthly-orders
      v-model="companyMealsMonthlyDialog"
      :company-data="selectedCompany"
    />

    <!-- Group Meals Monthly Orders Dialog -->
    <report-group-meals-taken-company-monthly-orders
      v-model="groupMealsMonthlyDialog"
      :firm-report-item="
        selectedCompany
          ? {
              company_id: selectedCompany.id,
              menu: selectedCompany.name,
              settings: selectedCompany.settings,
            }
          : {}
      "
    />

    <!-- Limits Settings Dialog -->
    <fullscreen-dialog
      v-model="limitsDialog"
      :title="$t('limits_settings')"
      icon="mdi-speedometer"
      :hide-footer="true"
    >
      <limits-card
        v-if="limitsDialog && editedItem.id"
        :company="editedItem"
        :restaurant="null"
        :wide-layout="true"
      />
    </fullscreen-dialog>
  </div>
</template>

<script>
import hasPrinting from "@/mixins/hasPrinting";
import hasSnackbar from "@/mixins/hasSnackbar";
import CompanyFormDialog from "@/components/companies/dialogs/CompanyFormDialog.vue";
import CompanyDeliverySettingsDialog from "@/components/companies/CompanyDeliverySettingsDialog.vue";
import CompanyCreditDialog from "@/components/companies/dialogs/CompanyCreditDialog.vue";
import CompanyPartnerDialog from "@/components/companies/dialogs/CompanyPartnerDialog.vue";
import CompanySettingsDialog from "@/components/companies/dialogs/CompanySettingsDialog.vue";
import LimitsCard from "@/components/companies/delivery-settings/LimitsCard.vue";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import SearchUserDialog from "@/components/companies/dialogs/SearchUserDialog.vue";
import PageHeader from "@/components/global/PageHeader.vue";
import PageToolbar from "@/components/global/PageToolbar.vue";
import { defaultCompany } from "@/models/company";
import { createWeeklyTimeSettings } from "@/models/timeSettings";
import menuService from "@/services/menu";
import companyService from "@/services/company";
import ReportCompanyMealsTakenMonthlyOrders from "@/components/report/meals/dialogs/ReportCompanyMealsTakenMonthlyOrders.vue";
import ReportGroupMealsTakenCompanyMonthlyOrders from "@/components/report/group-meals/dialogs/ReportGroupMealsTakenCompanyMonthlyOrders.vue";

export default {
  mixins: [hasPrinting, hasSnackbar],
  components: {
    CompanyFormDialog,
    CompanyDeliverySettingsDialog,
    CompanyCreditDialog,
    CompanyPartnerDialog,
    CompanySettingsDialog,
    LimitsCard,
    FullscreenDialog,
    SearchUserDialog,
    PageHeader,
    PageToolbar,
    ReportCompanyMealsTakenMonthlyOrders,
    ReportGroupMealsTakenCompanyMonthlyOrders,
  },
  data: () => ({
    companyFormDialog: false,
    deliverySettingsDialog: false,
    partnerDialog: false,
    limitsDialog: false,
    selectedCompany: null,
    search: "",
    creditDialog: false,
    settingsDialog: false,
    menuTypes: [],
    companies: [],
    editedIndex: -1,
    editedParentIndex: -1,
    company_type: [],
    editedItem: { ...defaultCompany },
    searchUserDialog: false,
    selectedRestaurant: null,
    isLoading: false,
    expandedRows: [],
    companyMealsMonthlyDialog: false,
    groupMealsMonthlyDialog: false,
  }),
  computed: {
    user() {
      return this.$store.state.user.user;
    },
    headers() {
      return [
        {
          text: "ID",
          align: "start",
          value: "id",
          width: "80px",
        },
        {
          text: this.$t("title"),
          align: "start",
          value: "name",
        },
        {
          text: this.$t("shifts"),
          align: "start",
          value: "shifts",
          sortable: false,
        },
        {
          text: this.$t("menu_type"),
          align: "start",
          value: "type",
          sortable: false,
        },
        {
          text: this.$t("credit_only"),
          align: "start",
          value: "is_credit",
          sortable: false,
        },
        {
          text: this.$t("users"),
          align: "center",
          value: "users_count",
          sortable: false,
        },
        {
          text: this.$t("branches"),
          align: "center",
          value: "branches",
          sortable: false,
          width: "200px",
        },
        {
          text: this.$t("actions"),
          align: "end",
          value: "actions",
          sortable: false,
          width: "200px",
        },
      ];
    },
    filteredCompanies() {
      let result = this.companies;

      // Filter by company type
      if (this.company_type.length) {
        result = result.filter((company) => this.company_type.includes(company.contract));
      }

      // Filter by search term
      if (this.search) {
        const searchTerm = this.translate(this.search.toLowerCase());
        result = result.filter((company) => {
          const nameMatch = this.translate(company.name.toLowerCase()).includes(
            searchTerm
          );
          const idMatch = String(company.id ?? "").includes(searchTerm.trim());
          const childMatch = company.children?.some((child) =>
            this.translate(child.name.toLowerCase()).includes(searchTerm)
          );
          return nameMatch || idMatch || childMatch;
        });
      }

      return result;
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    getShiftEnabled(item, n) {
      const map = {
        1: "shift_one",
        2: "shift_two",
        3: "shift_three",
        4: "shift_four",
      };
      const key = map[n];
      if (!item) return false;
      if (item.contract === 4) {
        return !!(
          item.restaurant?.pivot?.settings && item.restaurant.pivot.settings[key]
        );
      }
      return !!item[key];
    },
    /** Posle snimanja podešavanja veze ponovo učitava restorane firme i otvara svježe stanje sa servera. */
    async reloadRestaurantSettings() {
      const restaurantId = this.selectedRestaurant?.id;
      const restaurants = await this.$refs.partnerDialog?.loadPartners();
      const fresh = (restaurants || []).find((item) => item.id === restaurantId);
      if (fresh) {
        this.openRestaurantSettings(fresh);
      }
    },
    openRestaurantSettings(restaurant) {
      this.selectedRestaurant = restaurant;

      if (!this.selectedRestaurant.time_settings) {
        this.selectedRestaurant.time_settings = {};
        this.selectedRestaurant.time_settings.days = createWeeklyTimeSettings();
      }

      this.deliverySettingsDialog = true;
    },
    toggleExpand(item) {
      const index = this.expandedRows.findIndex((i) => i.id === item.id);
      if (index > -1) {
        this.expandedRows.splice(index, 1);
      } else {
        this.expandedRows.push(item);
      }
    },
    isItemExpanded(item) {
      return this.expandedRows.some((i) => i.id === item.id);
    },
    getCompanyAvatarColor(company) {
      const colors = [
        "#2196F3",
        "#4CAF50",
        "#FF9800",
        "#9C27B0",
        "#00BCD4",
        "#F44336",
        "#FF5722",
        "#795548",
        "#607D8B",
        "#3F51B5",
      ];
      const index = (company.id || 0) % colors.length;
      return colors[index];
    },
    getCompanyTypeColor(company) {
      // Pronađi menu type u listi
      const menuType = this.menuTypes?.find((mt) => mt.name === company.type);

      if (!menuType) return "grey lighten-2";

      // Ako je globalni tip (restaurant_id === null), koristi fiksnu boju
      if (menuType.restaurant_id === null) {
        const globalTypeColors = {
          "Tip 1": "green",
          "Tip 2": "blue",
          group: "purple",
          guest_meal: "orange",
          group_food: "deep-purple",
          table: "teal",
          more_restaurants: "cyan",
        };
        return globalTypeColors[menuType.name] || "indigo";
      }

      // Za custom tipove, generiši konzistentnu boju na osnovu ID-ja
      const colors = [
        "pink",
        "red",
        "deep-orange",
        "amber",
        "lime",
        "light-green",
        "light-blue",
        "blue-grey",
        "brown",
        "grey darken-1",
      ];
      const index = menuType.id % colors.length;
      return colors[index];
    },
    getCompanyTypeLabel(company) {
      // Return translated type label
      if (company.contract === 4) {
        return (
          this.$t(company.restaurant?.pivot.menu_types_id) ||
          company.restaurant?.pivot.menu_types_id ||
          "N/A"
        );
      }
      return this.$t(company.type) || company.type || "N/A";
    },
    getTotalUsersCount(company) {
      return company.users_count || 0;
    },
    goToUsers(company) {
      this.$router.push({
        name: "firma-id-korisnici",
        params: {
          id: company.id,
          name: company.name,
          parent_id: company.parent_id,
          children: company.children,
        },
      });
    },
    changeEveryDayOffer() {
      if (this.editedItem.id) {
        companyService
          .changeEveryDayOffer(this.editedItem)
          .then(({ data }) => {
            if (data) {
              this.settingsDialog = false;
              this.showSuccessSnackbar(this.$t("everyday_offer_updated"));
            }
          })
          .catch(() => {
            this.showErrorSnackbar(this.$t("error_updating_everyday_offer"));
          });
      }
    },

    openSettings(item) {
      this.selectedCompany = item;
      this.settingsDialog = true;
    },
    openCredit(item) {
      this.selectedCompany = item;
      this.creditDialog = true;
    },
    setCredit(item, index) {
      companyService
        .setIsCredit(item.id, item)
        .then(() => {
          this.showSuccessSnackbar(this.$t("credit_type_updated"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_updating_credit_type"));
        });
    },
    async initialize() {
      this.isLoading = true;
      try {
        const [companiesRes, menuTypesRes] = await Promise.all([
          companyService.getCompanies(),
          menuService.getMenuTypes(),
        ]);

        this.companies = companiesRes.data.map((item) => {
          return {
            ...item,
            shiftTransation: item.shift_translations || [],
            children: (item.children || []).map((child) => ({
              ...child,
              shiftTransation: child.shift_translations || [],
            })),
          };
        });

        this.menuTypes = menuTypesRes.data.map((type) => ({
          ...type,
          filteredName: this.$t(type.name) || type.name,
        }));
      } catch (error) {
        this.showErrorSnackbar(this.$t("error_loading_companies"));
      } finally {
        this.isLoading = false;
      }
    },
    editItem(item) {
      if (item.parent_id) {
        this.editedIndex = this.companies.findIndex((i) => i.id === item.parent_id);
        this.editedParentIndex = this.companies[this.editedIndex].children.findIndex(
          (i) => i.id === item.id
        );
      } else {
        this.editedIndex = this.companies.indexOf(item);
      }
      this.editedItem = Object.assign({}, item);
      this.companyFormDialog = true;
    },
    deleteItem(item) {
      if (item.parent_id) {
        this.editedIndex = this.companies.findIndex((i) => i.id === item.parent_id);
        this.editedParentIndex = this.companies[this.editedIndex].children.findIndex(
          (i) => i.id === item.id
        );
      } else {
        this.editedIndex = this.companies.indexOf(item);
      }
      this.editedItem = Object.assign({}, item);

      this.$refs.confirmBox
        .open(
          this.$t("delete_confirmation"),
          `${this.$t("confirm_delete_company")}: ${item.name}?`,
          {
            color: "error",
          }
        )
        .then(() => {
          companyService
            .deleteCompany(this.editedItem.id)
            .then((res) => {
              if (this.editedParentIndex > -1) {
                this.companies[this.editedIndex].children.splice(
                  this.editedParentIndex,
                  1
                );
              } else {
                this.companies.splice(this.editedIndex, 1);
              }
              // Reset the edited item and indices
              this.editedItem = Object.assign({}, defaultCompany);
              this.editedIndex = -1;
              this.editedParentIndex = -1;
              this.showSuccessSnackbar(this.$t("company_deleted"));
            })
            .catch(() => {
              this.showErrorSnackbar(this.$t("error_deleting_company"));
            });
        })
        .catch((err) => console.warn(err));
    },
    close() {
      this.companyFormDialog = false;
      this.$nextTick(() => {
        this.editedItem = Object.assign({}, defaultCompany);
        this.editedIndex = -1;
        this.editedParentIndex = -1;
      });
    },
    handleCompanyUpdated(data) {
      const { item, editedIndex, editedParentIndex } = data;

      if (editedParentIndex > -1) {
        // Update child company
        Object.assign(this.companies[editedIndex].children[editedParentIndex], item);
      } else {
        // Update parent company
        Object.assign(this.companies[editedIndex], item);
      }

      this.$forceUpdate();
    },
    handleCompanyCreated(data) {
      const { item } = data;

      if (item.parent_id) {
        // Add as child company
        const parentIndex = this.companies.findIndex((i) => i.id === item.parent_id);
        if (parentIndex !== -1) {
          if (!this.companies[parentIndex].children) {
            this.companies[parentIndex].children = [];
          }
          this.companies[parentIndex].children.push(item);
        }
      } else {
        // Add as parent company
        this.companies.push(item);
      }

      this.$forceUpdate();
    },
    print() {
      const printHeaders = [
        { text: "ID", value: "id" },
        { text: this.$t("title"), value: "name" },
        { text: this.$t("type"), value: "type" },
      ];

      const printItems = this.filteredCompanies.map((company) => {
        return {
          id: company.id,
          name: this.toLatin(company.name),
          type: this.getCompanyTypeLabel(company),
        };
      });

      this.printDocument({
        title: this.$t("companies"),
        tables: [
          {
            headers: printHeaders,
            items: printItems,
          },
        ],
      });
    },
    openPartner(item) {
      this.selectedCompany = item;
      this.partnerDialog = true;
    },
    openMonthlyOrdersReport(item) {
      this.selectedCompany = item;
      if (item.type === "group") {
        this.groupMealsMonthlyDialog = true;
      } else {
        this.companyMealsMonthlyDialog = true;
      }
    },
  },
  watch: {
    companyFormDialog(val) {
      val || this.close();
    },
  },
};
</script>
<style scoped>
.data-table-card >>> .child-table {
  background-color: #fafafa !important;
}

.data-table-card >>> .child-table tbody tr {
  background-color: #fafafa !important;
}

/* Sakrivamo expand kolonu potpuno */
.data-table-card >>> .v-data-table__expand-icon {
  display: none !important;
}

/* Th expand kolonu sakrivamo - width 0 i nevidljivo */
.data-table-card >>> .v-data-table-header tr th:first-child {
  width: 0 !important;
  max-width: 0 !important;
  min-width: 0 !important;
  padding: 0 !important;
  border: none !important;
  display: none !important;
  pointer-events: none !important;
}

/* Child table - sakrivamo expand td */
.data-table-card >>> .child-table tbody tr > td:first-child {
  display: table-cell !important;
}
/* Td expand kolonu sakrivamo potpuno */
.data-table-card >>> tbody tr > td.text-start:first-child {
  display: none;
}

/* Custom expand dugme */
.expand-button {
  text-transform: none !important;
  letter-spacing: normal !important;
  font-size: 0.75rem !important;
}

.expand-button:hover {
  background-color: rgba(33, 150, 243, 0.08) !important;
}

@media (max-width: 599px) {
  .page-content {
    padding: 8px;
  }
}
</style>
